/**
 * Single source of truth for route paths. Import these instead of writing
 * string literals at call sites, so a URL change is a one-line edit here.
 *
 * ## What may go in a URL
 *
 * A path is the least private thing about a request. It is kept in browser
 * history, read by extensions, sent as `Referer` on the way to another origin,
 * and caught in any screenshot of the address bar — all of it outside the
 * app's control and none of it cleared by signing out.
 *
 * So a route param carries an opaque, backend-issued surrogate and nothing
 * else. `:clientId` stands for the `public_id` — a UUID — and never the
 * database key behind it. Two properties are what matter, and both have to
 * hold:
 *
 *   - Not enumerable. Sequential keys let anyone holding one URL walk the rest
 *     by counting, so every hit tells them a record exists. A random id gives
 *     no next value to try.
 *   - Not descriptive. No name, email, phone, or anything else read off the
 *     record, in a param or a query string. `?tab=` names a tab, which is why
 *     it is allowed to sit in the URL at all.
 *
 * On top of those, the id is enciphered into a token by `routeIds` before it
 * goes in the path, so the URL does not read as a record over a shoulder or in
 * a screenshot. That is a fourth layer and the weakest one — the key ships in
 * the bundle, so it stops a reader, not an attacker. Read the header of
 * `routeIds.ts` before relying on it for anything.
 *
 * None of this is the actual access control. The server decides that, and
 * already does: an id belonging to another workspace answers 404, not the
 * record (see `API_ENDPOINTS.clients.detail`). These keep a URL from being a
 * place to start looking — they do not stand in for the check behind it, and a
 * decoded id is as untrusted as one typed into the bar.
 *
 * `:planId` is a plan's `public_id`, the same way `:clientId` is a client's:
 * `POST /api/v1/meal-plans/` issues it, and the create dialog navigates
 * straight to it. The seeded fixtures keep their readable `seed-plan-N` until
 * the mock goes, which is the day a plan's meals can be fetched rather than
 * held in the browser by `plansService`.
 */
import { encodeRouteId } from "./routeIds";

export const ROUTES = {
  // Auth
  signup: "/signup",
  /** The DPDP consent gate. Sits between the sign-up screen and the account
   *  actually being created, for both the email and the Google route in. */
  consent: "/consent",
  verifyOtp: "/verify-otp",
  login: "/login",
  /** Where Clerk returns the browser after a social sign-in. */
  ssoCallback: "/sso-callback",

  // App
  /**
   * The same terms put to a user who is already signed in - a document
   * published or republished since they last agreed.
   *
   * A separate path from `consent` above, and deliberately on this side of the
   * list: that one is closed to anyone signed in, and this is only ever reached
   * while signed in. One path serving both would redirect against itself.
   */
  consentRequired: "/consent-required",
  dashboard: "/dashboard",
  myClients: "/my-clients",
  // Static, so it wins over `myClientDetail` below: the router ranks a literal
  // segment above a param, and no client can be given the id "new".
  addClient: "/my-clients/new",
  myClientDetail: "/my-clients/:clientId",
  /**
   * The Add client form again, opened on a client who already exists.
   *
   * Under the detail path rather than beside it, so the URL says which record
   * is being edited — and a segment longer than `myClientDetail`, so the
   * router cannot confuse the two.
   */
  editClient: "/my-clients/:clientId/edit",
  enquiries: "/enquiries",
  // Static, so it wins over `enquiryDetail` below — same reasoning as
  // `addClient` above `myClientDetail`.
  addEnquiry: "/enquiries/new",
  enquiryDetail: "/enquiries/:enquiryId",
  /**
   * The Add enquiry form again, opened on an enquiry that already exists.
   *
   * Under the detail path rather than beside it, so the URL says which record
   * is being edited — and a segment longer than `enquiryDetail`, so the router
   * cannot confuse the two.
   */
  editEnquiry: "/enquiries/:enquiryId/edit",
  createPlan: "/create-plan",
  /** The plan builder, keyed on the id the plan list's rows carry. */
  planDetail: "/create-plan/:planId",
  /**
   * The supplement builder, keyed on the client it is being written for.
   *
   * On the client rather than on a record of its own, unlike `planDetail`
   * above: one pass of the screen writes several supplements at once, so there
   * is no single thing for the path to name until they are stored. It is only
   * ever opened from a client's Plans tab, which is where the id comes from.
   */
  createSupplement: "/create-supplement/:clientId",
  /**
   * The practice's own dishes - what it has written or uploaded, with the
   * macros each one carries.
   *
   * Nothing in the path but the word: the screen is one list, and a dish is
   * opened nowhere yet. No param to keep opaque, so nothing above applies.
   */
  dishes: "/dishes",
  revenue: "/revenue",
  myBrand: "/my-brand",
  createPage: "/create-page",
  launchApp: "/launch-app",
  /**
   * What the dietitian pays Naurist, as it used to be reached.
   *
   * No longer a screen: the plans on offer, the plan in force and the invoice
   * history are the Settings screen's Billings tab now — see
   * `BILLING_SETTINGS_PATH`. This path stays for two reasons, and redirects
   * there: it is in browser histories and bookmarks, and it is still the parent
   * of `billingCheckout` below, which is a screen of its own.
   */
  billing: "/billing",
  /**
   * The checkout for one plan — who it is billed to, and what it comes to.
   *
   * `:cycle` is the plain word: `/billing/yearly`. The only param in this file
   * that is not an opaque id, and deliberately so — everything the rule above
   * guards against needs the param to stand for a record. This one names a
   * product cycle out of a list of two that is the same for every user: there
   * is no next value to walk to, and nothing about a person in it. It is a
   * `?tab=` that happens to sit in the path.
   */
  billingCheckout: "/billing/:cycle",
  /**
   * How a checkout ended.
   *
   * A route rather than a step of the checkout for one reason: a card can be
   * authorised through a 3-D Secure redirect at the issuer, and the gateway
   * has to be given a `callback_url` to return to. What it returns to is a
   * fresh page load carrying no router state, so the outcome is handed over in
   * session storage — see `checkoutMachine`.
   */
  billingResult: "/billing/:cycle/result",
  /**
   * Where a new account lands: the free trial, offered before the app is.
   *
   * Three screens under one path — the plan, who it is billed to, and the
   * confirmation — and none of them is inside `AppLayout`, so the sidebar
   * appears for the first time on the dashboard the last of them leads to.
   * Signing up is the only thing that points here; see `VerifyOtpPage` and
   * `ConsentPage`.
   *
   * `/create-account` rather than `/start-trial`, because that is what these
   * three screens are to the person walking them: the last stretch of signing
   * up, not a separate offer they went looking for. The URL is read in the
   * address bar and in any link to it, and it should say where somebody is —
   * finishing an account — rather than name the thing being sold on the way.
   */
  trial: "/create-account",
  /**
   * The trial's own checkout: the details an invoice will need once the
   * fourteen days are up.
   *
   * `:cycle` is the plain word — `/create-account/yearly` — exactly as
   * `billingCheckout` carries it, and for the reason written there: it names
   * one of two product cycles rather than a record. Which cycle was picked on
   * the screen before is in the URL rather than in router state so a reload
   * does not drop somebody back to the start of the flow.
   */
  trialDetails: "/create-account/:cycle",
  /** The trial, started. Longer than the path above it, so the router ranks it
   *  first — the same ordering `addClient` gets above the client detail. */
  trialStarted: "/create-account/:cycle/started",
  settings: "/settings",
  /**
   * Picking a time for a support call — Cal's booker, on a screen of its own.
   *
   * Under `/settings` because that is the only way in: it is the second half of
   * the Support tab's "Book a call", not a destination of its own. A screen
   * rather than a dialog or a panel because a month of availability is a page's
   * worth of screen, and because a booking is a thing somebody leaves the
   * settings to do and comes back from.
   *
   * Nothing about the request is in the path. What was typed into the form
   * travels as router state — see `SupportTab` — since a reason and a message
   * are exactly the descriptive detail the note above this list forbids in a
   * URL. Reached with no state, the booker simply opens unfilled.
   */
  bookCall: "/settings/book-call",
} as const;

export type RoutePath = (typeof ROUTES)[keyof typeof ROUTES];

/** Marketing-site pages that live outside this app. */
export const EXTERNAL_LINKS = {
  /**
   * The marketing site's front page — where someone goes when the app itself
   * has nothing to show them, as on a viewport it will not run at.
   */
  home: "https://naurist.com/",
  privacyPolicy: "https://naurist.com/privacy-policy",
  termsOfUse: "https://naurist.com/terms-of-use",
  dataProcessingAgreement: "https://naurist.com/data-processing-agreement",
  refundPolicy: "https://naurist.com/refund-policy",
} as const;

/**
 * Fills the `:clientId` param, so call sites never build the URL by hand.
 *
 * The id goes in enciphered — see `routeIds` — so the path does not read as a
 * record to anyone who happens to see the address bar. Pass the stored
 * `public_id`; the screen at the other end decodes it back with
 * `decodeRouteId`. Call sites deal in real ids only, and no call site should
 * encode one itself.
 *
 * The screen's tab is a `?tab=` on the end rather than state it holds alone,
 * so a link can point at one — which is what lets the plan builder send
 * someone back to the Plans tab they started from. `tab` is a
 * `ClientDetailTabId`; the union lives with the screen that renders it, and an
 * unknown value there simply opens on Details. It stays in the clear: it names
 * a tab, not a person.
 *
 * `kind` is the same again one level down, for the Plans tab's three lists —
 * "meal", "supplement" or "exercise". The supplement builder leaves the screen
 * and comes back to it, and without this it would come back to the Meal list
 * whichever list it was opened from. Only read under `tab=plans`, and an
 * unknown value opens on Meal, so it is safe to leave off.
 */
export const clientDetailPath = (
  clientId: string,
  tab?: string,
  kind?: string
) => {
  const token = encodeRouteId(clientId);
  if (!tab) return `${ROUTES.myClients}/${token}`;

  const query = kind ? `tab=${tab}&kind=${kind}` : `tab=${tab}`;
  return `${ROUTES.myClients}/${token}?${query}`;
};

/** The edit form for one client — the detail path with `/edit` on the end. */
export const clientEditPath = (clientId: string) =>
  `${ROUTES.myClients}/${encodeRouteId(clientId)}/edit`;

/** The same, for `:planId`. */
export const planDetailPath = (planId: string) =>
  `${ROUTES.createPlan}/${encodeRouteId(planId)}`;

/**
 * The supplement builder for one client — enciphered like every other id.
 *
 * `supplementId` opens it on one that already exists, as a `?supplement=`
 * rather than another segment: the screen is the same screen either way, and
 * the path already names the client it writes for. Enciphered too, for the
 * same reason the client is.
 */
export const createSupplementPath = (clientId: string, planId?: string) => {
  const path = `/create-supplement/${encodeRouteId(clientId)}`;
  return planId ? `${path}?plan=${encodeRouteId(planId)}` : path;
};

/** The same, for `:enquiryId`. */
export const enquiryDetailPath = (enquiryId: string) =>
  `${ROUTES.enquiries}/${encodeRouteId(enquiryId)}`;

/** The edit form for one enquiry — the detail path with `/edit` on the end. */
export const enquiryEditPath = (enquiryId: string) =>
  `${enquiryDetailPath(enquiryId)}/edit`;

/**
 * The checkout for one plan cycle — `billingCheckoutPath("yearly")`.
 *
 * No encoding, unlike the builders above: the cycle is a word from a fixed list
 * of two, not an id standing for a record. See `billingCheckout`.
 */
export const billingCheckoutPath = (cycle: string) =>
  `${ROUTES.billing}/${cycle}`;

/** How that checkout ended — the checkout path with `/result` on the end. */
export const billingResultPath = (cycle: string) =>
  `${billingCheckoutPath(cycle)}/result`;

/**
 * The trial's detail step for one cycle — `trialDetailsPath("yearly")`.
 *
 * No encoding, for the same reason `billingCheckoutPath` does none: the cycle
 * is a word from a fixed list of two, not an id standing for a record.
 */
export const trialDetailsPath = (cycle: string) => `${ROUTES.trial}/${cycle}`;

/** The trial, started — the detail path with `/started` on the end. */
export const trialStartedPath = (cycle: string) =>
  `${trialDetailsPath(cycle)}/started`;

/**
 * One tab of the Settings screen — `settingsTabPath("billings")`.
 *
 * The tab is a `?tab=` rather than a path of its own, the way a client's
 * detail tabs are, and for the same reason: it names a tab, not a record, so it
 * is allowed in the URL in the clear — and a link can point at one, which is
 * what everything below relies on.
 */
export const settingsTabPath = (tab: string) => `${ROUTES.settings}?tab=${tab}`;

/**
 * One tab of the My brand screen — `brandTabPath("plans")`.
 *
 * The same `?tab=` Settings carries, and allowed in the URL for the same
 * reason: it names a tab, not a record.
 */
export const brandTabPath = (tab: string) => `${ROUTES.myBrand}?tab=${tab}`;

/**
 * Where plans live: the My brand screen, opened on its Plans tab.
 *
 * The dashboard's setup checklist is what points at it — a step that says
 * "Create plans" has to land on the list that creates them, not on the
 * brand details beside it.
 */
export const BRAND_PLANS_PATH = brandTabPath("plans");

/**
 * Where services live: the My brand screen, opened on its Services tab.
 *
 * The other half of what the Add client and New enquiry gate asks for — a
 * prompt that says a service is missing has to land on the grid that makes
 * one. See `useSetupGate`.
 */
export const BRAND_SERVICES_PATH = brandTabPath("services");

/**
 * Where billing lives: the Settings screen, opened on its Billings tab.
 *
 * Every way back to billing goes through this — the checkout's breadcrumb, the
 * result screen's way out, the redirect off `/billing` itself — so moving the
 * section again is a one-line edit here rather than a hunt through the feature.
 */
export const BILLING_SETTINGS_PATH = settingsTabPath("billings");

/**
 * Where support lives: the Settings screen, opened on its Support tab.
 *
 * The way back from the booking screen, which is reached from nowhere else — so
 * the trail it draws leads to the tab that sent it, not to Settings in general.
 */
export const SUPPORT_SETTINGS_PATH = settingsTabPath("support");

/**
 * Where the Dish search setting lives: the My brand screen, opened on its
 * Configuration tab.
 *
 * The plan builder's switch points here twice — the "Settings" under it and
 * the "Learn more" on the card that introduces it — so both land on the tab
 * that explains the choice rather than on the brand's details.
 */
export const BRAND_CONFIGURATION_PATH = brandTabPath("configuration");
